import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { COMPANY_CATEGORIES } from "../../data/companyCategories";
import { ensureHttpsUrl } from "../../utils/website";
import { InfoTooltip } from "../../components/InfoTooltip";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";

function filterCategories(query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return COMPANY_CATEGORIES;
  return COMPANY_CATEGORIES.filter((c) => c.title.toLowerCase().includes(q));
}

const BILLING_TYPES = [
  "Stripe","Net 30","CJ Affiliate","Impact","ShareASale","Awin","Rakuten Advertising","Partnerize",
  "ClickBank","FlexOffers","Pepperjam","Admitad","Tradedoubler","Everflow","Tune","Refersion",
  "LeadDyno","Tapfiliate","FirstPromoter","Rewardful","Post Affiliate Pro","AffiliateWP","LTK",
  "ShopMy","MagicLinks","Skimlinks","Sovrn Commerce","Amazon Associates","eBay Partner Network",
  "Shopify Collabs","Walmart Affiliates","MaxBounty","CrakRevenue","ClickDealer","Perform[cb]",
  "AdCombo","DrCash",
];

function filterBillingTypes(query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return BILLING_TYPES;
  return BILLING_TYPES.filter((opt) => opt.toLowerCase().includes(q));
}

function normalizeWebsite(url) {
  return (url || "").trim().toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "");
}

export function NewAdvertiserModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [advertiserId, setAdvertiserId] = useState("");
  const [billingType, setBillingType] = useState("");
  const [billingTypeahead, setBillingTypeahead] = useState("");
  const [billingDropdownOpen, setBillingDropdownOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [categoryTypeahead, setCategoryTypeahead] = useState("");
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [brandContext, setBrandContext] = useState("");
  const [brandSuitability, setBrandSuitability] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const motion = useModalMotion(onClose, { dismissible: !saving });
  const { requestClose } = motion;
  const [saveError, setSaveError] = useState(null);
  const billingInputRef = useRef(null);
  const categoryInputRef = useRef(null);

  const filteredBilling = filterBillingTypes(billingTypeahead);
  const filteredCategories = filterCategories(categoryTypeahead);

  useEffect(() => {
    if (billingType) setBillingTypeahead(billingType);
  }, [billingType]);

  const inputStyle = {
    width:"100%", background:"#FAFAFA", border:"1px solid #E2E2E2",
    borderRadius:10, padding:"10px 14px", color:"#151515",
    fontSize:14, outline:"none", boxSizing:"border-box", fontFamily:"inherit",
  };

  const labelStyle = {
    display:"block", fontSize:11, color:"#626262",
    textTransform:"uppercase", letterSpacing:1, marginBottom:6,
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) { setSaveError("Name is required."); return; }
    if (!supabase) { setSaveError("Supabase not configured."); return; }
    setSaveError(null);
    setSaving(true);
    const normalizedWebsite = ensureHttpsUrl(website);
    if (normalizedWebsite) {
      const normalized = normalizeWebsite(normalizedWebsite);
      const { data: candidates } = await supabase
        .from("advertisers")
        .select("id, name, website")
        .ilike("website", `%${normalized}%`);
      const existing = (candidates || []).find(
        (a) => normalizeWebsite(a.website) === normalized
      );
      if (existing) {
        setSaveError(`Website already exists: "${existing.name}" (ID ${existing.id}) — ${existing.website}`);
        setSaving(false);
        return;
      }
    }
    if (normalizedWebsite !== website) {
      setWebsite(normalizedWebsite);
    }
    const payload = {
      name: trimmedName,
      website: normalizedWebsite || null,
      billing_type: billingType.trim() || null,
      category: category || null,
      brand_context: brandContext.trim() || null,
      brand_suitability: brandSuitability.trim() || null,
      // New advertisers start in onboarding (this also matches the DB default);
      // support activates them from AdvertiserSettingsModal once creatives and
      // campaign settings are in place.
      status: "onboarding",
      updated_at: new Date().toISOString(),
    };
    const idNum = advertiserId.trim() ? parseInt(advertiserId.trim(), 10) : null;
    if (idNum != null && !isNaN(idNum)) payload.id = idNum;
    const { data: insertedRow, error } = await supabase
      .from("advertisers")
      .insert(payload)
      .select("id")
      .maybeSingle();
    setSaving(false);
    if (error) { setSaveError(error.message); return; }

    // Map brand_suitability to brand_safety_filter_target when the note is non-blank.
    const newNote = (brandSuitability ?? "").trim();
    if (newNote && insertedRow?.id) {
      supabase.functions.invoke("map-brand-suitability", {
        body: { advertiser_id: insertedRow.id, expected_note: newNote },
      }).then(({ error: invokeError }) => {
        if (invokeError) console.warn("map-brand-suitability invoke failed:", invokeError);
      }).catch((err) => console.warn("map-brand-suitability invoke failed:", err));
    }

    onCreated?.();
    requestClose();
  };

  const canSubmit = name.trim().length > 0 && !saving;

  const handleSuggestCategory = async () => {
    const url = ensureHttpsUrl(website);
    if (!url || !supabase) return;
    if (url !== website) {
      setWebsite(url);
    }
    setSuggestLoading(true);
    setSuggestions([]);
    try {
      const { data, error } = await supabase.functions.invoke("suggest-advertiser-category", { body: { url } });
      setSuggestions((!error && data?.suggestions) ? data.suggestions : []);
    } catch {
      setSuggestions([]);
    } finally {
      setSuggestLoading(false);
    }
  };

  const dropdownStyle = {
    position:"absolute", top:"100%", left:0, right:0,
    background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:10,
    zIndex:20, maxHeight:220, overflowY:"auto", marginTop:4,
    boxShadow:"0 4px 12px rgba(0,0,0,0.12)",
  };

  return (
    <ModalShell motion={motion} labelledBy="new-advertiser-title" maxWidth={520}>
        <div style={{ padding:"28px 28px 20px", borderBottom:"1px solid #F0F0F0" }}>
          <button onClick={requestClose} aria-label="Close" style={{ position:"absolute", top:16, right:20, background:"none", border:"none", color:"#626262", fontSize:20, cursor:"pointer" }}>✕</button>
          <div style={{ fontSize:11, color:"#F90091", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Support</div>
          <h2 id="new-advertiser-title" style={{ fontSize:20, fontWeight:600, color:"#151515", margin:0, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Create New Advertiser</h2>
        </div>
        <div style={{ padding:"24px 28px" }}>
          <div style={{ marginBottom:16 }}>
            <label style={labelStyle}>Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Advertiser name" style={inputStyle} />
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={labelStyle}>Website</label>
            <input
              type="text"
              value={website}
              onChange={(e) => {
                setWebsite(e.target.value);
                setSuggestions([]);
              }}
              onBlur={() => {
                const normalized = ensureHttpsUrl(website);
                if (normalized !== website) {
                  setWebsite(normalized);
                }
              }}
              placeholder="https://..."
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom:20 }}>
            <label style={labelStyle}>Category</label>
            <div style={{ position:"relative" }} onFocus={() => setCategoryDropdownOpen(true)} onBlur={() => setTimeout(() => setCategoryDropdownOpen(false), 180)}>
              <div style={{ display:"flex", gap:8 }}>
                <input
                  ref={categoryInputRef}
                  type="text"
                  value={categoryTypeahead}
                  onChange={(e) => { setCategoryTypeahead(e.target.value); setCategory(""); }}
                  onFocus={() => setCategoryDropdownOpen(true)}
                  placeholder="Type to search IAB categories..."
                  style={{ ...inputStyle, flex:1, width:"auto" }}
                />
                <button
                  type="button"
                  disabled={!website.trim() || suggestLoading}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setCategoryDropdownOpen(false);
                    handleSuggestCategory();
                  }}
                  style={{
                    flexShrink:0,
                    padding:"9px 12px",
                    background:"#FFF5FB",
                    border:"1px solid #F9009133",
                    borderRadius:10,
                    color:"#F90091",
                    fontSize:12,
                    fontWeight:600,
                    cursor: !website.trim() || suggestLoading ? "not-allowed" : "pointer",
                    opacity: !website.trim() || suggestLoading ? 0.5 : 1,
                    whiteSpace:"nowrap",
                  }}
                >
                  {suggestLoading ? "…" : "✦ Suggest"}
                </button>
              </div>
              {suggestions.length > 0 && (
                <div style={{ marginTop:8, display:"flex", flexWrap:"wrap", gap:6 }}>
                  {suggestions.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => {
                        setCategory(s.value);
                        setCategoryTypeahead(s.title);
                        setSuggestions([]);
                      }}
                      style={{
                        padding:"4px 12px",
                        background:"#FFF5FB",
                        border:"1px solid #F9009133",
                        color:"#F90091",
                        borderRadius:20,
                        fontSize:12,
                        fontWeight:600,
                        cursor:"pointer",
                        display:"flex",
                        alignItems:"center",
                        gap:6,
                      }}
                    >
                      {s.title}
                      <span style={{ opacity:0.6, fontWeight:400 }}>{Math.round(s.confidence * 100)}%</span>
                    </button>
                  ))}
                </div>
              )}
              {categoryDropdownOpen && (
                <div style={dropdownStyle}>
                  {filteredCategories.map((opt) => (
                    <div
                      key={opt.value}
                      onClick={() => { setCategory(opt.value); setCategoryTypeahead(opt.title); setCategoryDropdownOpen(false); categoryInputRef.current?.blur(); }}
                      style={{
                        padding:"10px 14px",
                        paddingLeft: opt.parent ? 14 : 28,
                        fontSize: opt.parent ? 12 : 13,
                        fontWeight: opt.parent ? 700 : 400,
                        color: opt.parent ? "#7b7b7b" : "#151515",
                        cursor: opt.parent ? "default" : "pointer",
                        background: category === opt.value ? "#FFF5FB" : "transparent",
                        borderTop: opt.parent ? "1px solid #F0F0F0" : "none",
                      }}
                      onMouseDown={opt.parent ? (e) => e.preventDefault() : undefined}
                    >
                      {opt.title}
                      <span style={{ marginLeft:6, fontSize:10, color:"#F90091" }}>{opt.value}</span>
                    </div>
                  ))}
                  {filteredCategories.length === 0 && <div style={{ padding:12, color:"#7b7b7b", fontSize:13 }}>No matches</div>}
                </div>
              )}
            </div>
          </div>
          <div style={{ marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
              <span style={{ ...labelStyle, marginBottom:0 }}>Context</span>
              <InfoTooltip text="Add brand positioning, messaging, and any other details that help our AI understand this advertiser." />
            </div>
            <textarea
              aria-label="Brand context"
              value={brandContext}
              onChange={(e) => setBrandContext(e.target.value)}
              placeholder="Describe the brand, its positioning, target audience, tone of voice, and anything else that helps the AI make better decisions."
              style={{ ...inputStyle, minHeight:90, resize:"vertical", lineHeight:1.55, padding:"10px 14px" }}
            />
          </div>

          <div style={{ marginBottom:20 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
              <span style={{ ...labelStyle, marginBottom:0 }}>Brand Suitability</span>
              <InfoTooltip text="List podcasts, shows, topics, or types of audio content where this brand should not appear." />
            </div>
            <textarea
              aria-label="Brand suitability"
              value={brandSuitability}
              onChange={(e) => setBrandSuitability(e.target.value)}
              placeholder="e.g. true crime, political commentary, explicit content, competitor brand podcasts…"
              style={{ ...inputStyle, minHeight:90, resize:"vertical", lineHeight:1.55, padding:"10px 14px" }}
            />
          </div>

          <div style={{ marginBottom:16 }}>
            <label style={labelStyle}>Advertiser ID (optional)</label>
            <input type="text" value={advertiserId} onChange={(e) => setAdvertiserId(e.target.value)} placeholder="Leave blank to auto-generate" style={inputStyle} />
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={labelStyle}>Billing type</label>
            <div style={{ position:"relative" }} onFocus={() => setBillingDropdownOpen(true)} onBlur={() => setTimeout(() => setBillingDropdownOpen(false), 180)}>
              <input
                ref={billingInputRef}
                type="text"
                value={billingTypeahead}
                onChange={(e) => { setBillingTypeahead(e.target.value); setBillingType(e.target.value); }}
                onFocus={() => setBillingDropdownOpen(true)}
                placeholder="Type to search..."
                style={inputStyle}
              />
              {billingDropdownOpen && (
                <div style={dropdownStyle}>
                  {filteredBilling.map((opt) => (
                    <div
                      key={opt}
                      onClick={() => { setBillingType(opt); setBillingTypeahead(opt); setBillingDropdownOpen(false); billingInputRef.current?.blur(); }}
                      style={{ padding:"10px 14px", fontSize:13, color:"#151515", cursor:"pointer", background: billingType === opt ? "#FFF5FB" : "transparent" }}
                    >
                      {opt}
                    </div>
                  ))}
                  {filteredBilling.length === 0 && <div style={{ padding:12, color:"#7b7b7b", fontSize:13 }}>No matches</div>}
                </div>
              )}
            </div>
          </div>
          {saveError && (
            <div style={{ marginBottom:12, padding:12, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:10, color:"#e53935", fontSize:13 }}>
              {saveError}
            </div>
          )}
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{ width:"100%", padding:"13px 0", background: !canSubmit ? "#E2E2E2" : "#F90091", color: !canSubmit ? "#B8B8B8" : "#fff", border:"none", borderRadius:10, fontSize:14, fontWeight:600, cursor: !canSubmit ? "default" : "pointer" }}
          >
            {saving ? "Saving…" : "Create Advertiser"}
          </button>
        </div>
    </ModalShell>
  );
}
